import React, { useState, useEffect } from 'react';
import { Box, Paper, List, ListItem, ListItemButton, ListItemText, ListItemIcon, Snackbar, Alert, Divider, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import PeopleIcon from '@mui/icons-material/People';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import SendIcon from '@mui/icons-material/Send';
import HistoryIcon from '@mui/icons-material/History';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import api from '../../api';

import MyLeaveTab from './MyLeaveTab';
import TeamLeaveTab from './TeamLeaveTab';
import ApplyLeaveTab from './ApplyLeaveTab';
import ApprovalsTab from './ApprovalsTab';

const LeaveDashboard = () => {
  const theme = useTheme();
  const { dashboard } = theme;
  
  const empId = localStorage.getItem('emp_id');
  const [activeMenu, setActiveMenu] = useState('my_leave');
  const [snackbar, setSnackbar] = useState({ open: false, msg: '', type: 'success' });
  const showMsg = (msg, type = 'success') => setSnackbar({ open: true, msg, type });

  const [balances, setBalances] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [pendingLeaves, setPendingLeaves] = useState([]);
  const [historyLeaves, setHistoryLeaves] = useState([]);
  const [approvedNHDates, setApprovedNHDates] = useState([]);

  useEffect(() => {
    fetchData();
    if (activeMenu === 'apply_leave') fetchNHDates();
    // eslint-disable-next-line
  }, [activeMenu]);

  const fetchData = async () => {
    try {
      const [balRes, reqRes, appRes] = await Promise.all([
        api.get(`/leave/balances/${empId}`),
        api.get(`/leave/my-requests/${empId}`),
        api.get(`/leave/approvals/${empId}`)
      ]);
      setBalances(balRes.data || []);
      setMyRequests(reqRes.data || []);
      setPendingLeaves(appRes.data?.pending || []);
      setHistoryLeaves(appRes.data?.history || []);
    } catch (err) { console.error("Error fetching personal leave data", err); }
  };

  const fetchNHDates = async () => {
    try {
      const res = await api.get('/settings/national-holidays');
      const validHolidays = res.data.filter(nh => {
        if (!nh.is_active) return false;
        return !myRequests.find(req => req.start_date === nh.date && req.leave_type === 'NH' && req.status !== 'REJECTED');
      });
      setApprovedNHDates(validHolidays);
    } catch (err) { console.error("Could not fetch NH dates"); }
  };

  // UPDATED: NavItem with theme-based styling (matches Dashboard & RosterDashboard)
  const NavItem = ({ id, label, icon }) => (
    <ListItem disablePadding sx={{ mb: dashboard.navItem.marginBottom }}>
      <ListItemButton 
        selected={activeMenu === id} 
        onClick={() => setActiveMenu(id)} 
        sx={{
          borderRadius: dashboard.navItem.borderRadius,
          mx: dashboard.navItem.paddingX,
          py: dashboard.navItem.paddingY,
          '&.Mui-selected': { 
            bgcolor: 'primary.main', 
            color: 'white', 
            '&:hover': { bgcolor: 'primary.dark' },
            '& .MuiListItemIcon-root': { color: 'white' },
          },
          '&.Mui-selected .MuiListItemText-primary': { 
            color: 'white',
            fontWeight: 'bold',
          },
        }}
      >
        <ListItemIcon sx={{ 
          minWidth: dashboard.navItem.iconMinWidth, 
          color: activeMenu === id ? 'white' : 'inherit' 
        }}>
          {icon}
        </ListItemIcon>
        <ListItemText 
          primary={label} 
          primaryTypographyProps={{ 
            fontWeight: activeMenu === id ? 'bold' : 'medium', 
            fontSize: dashboard.navItem.primaryFontSize
          }} 
        />
      </ListItemButton>
    </ListItem>
  );

  // UPDATED: Section Header with theme-based styling (matches Dashboard & RosterDashboard)
  const SectionHeader = ({ title }) => (
    <Typography 
      variant="caption" 
      sx={{ 
        px: dashboard.sectionHeading.paddingX,
        py: dashboard.sectionHeading.paddingY,
        display: 'block', 
        color: dashboard.sectionHeading.color,
        fontWeight: dashboard.sectionHeading.fontWeight,
        mt: dashboard.sectionHeading.marginTop,
        fontSize: dashboard.sectionHeading.fontSize,
        letterSpacing: dashboard.sectionHeading.letterSpacing,
        textTransform: dashboard.sectionHeading.textTransform,
      }}
    >
      {title}
    </Typography>
  );

  return (
    <Paper elevation={2} sx={{ 
      display: 'flex', 
      height: dashboard.paper.minHeight,
      width: '100%', 
      borderRadius: dashboard.paper.borderRadius, 
      overflow: 'hidden',
      mt: dashboard.paper.marginTop
    }}>
      
      {/* LEFT SIDEBAR - Matching Dashboard & RosterDashboard style */}
      <Box sx={{ 
        width: dashboard.sidebar.width,
        minWidth: dashboard.sidebar.width,
        borderRight: 1, 
        borderColor: 'divider', 
        bgcolor: '#ffffff', 
        pt: dashboard.sidebar.paddingTop,
        pb: dashboard.sidebar.paddingBottom,
        px: dashboard.sidebar.paddingX,
        boxShadow: '1px 0 3px rgba(0,0,0,0.04)',
        zIndex: 1, 
        overflowY: 'auto',
        overflowX: 'hidden',
        height: '100%',
        '&::-webkit-scrollbar': {
          width: 4,
        },
        '&::-webkit-scrollbar-track': {
          background: '#f1f1f1',
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#c1c1c1',
          borderRadius: 2,
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: '#a8a8a8',
        },
      }}>
        <List component="nav" dense sx={{ py: 0 }}>
          
          <SectionHeader title="LEAVE" />
          <NavItem id="my_leave" label="My Leave" icon={<BeachAccessIcon fontSize="small" />} />
          
          <Divider sx={{ my: dashboard.divider.marginY, mx: dashboard.divider.marginX }} />

          <SectionHeader title="TEAM LEAVE" />
          <NavItem id="balance_overview" label="Balance Overview" icon={<PeopleIcon fontSize="small" />} />
          <NavItem id="team_summary" label="Team Summary" icon={<AnalyticsIcon fontSize="small" />} />

          <Divider sx={{ my: dashboard.divider.marginY, mx: dashboard.divider.marginX }} />

          <SectionHeader title="REQUEST" />
          <NavItem id="apply_leave" label="Apply Leave" icon={<SendIcon fontSize="small" />} />
          <NavItem id="apply_status" label="Apply Status" icon={<HistoryIcon fontSize="small" />} />

          <Divider sx={{ my: dashboard.divider.marginY, mx: dashboard.divider.marginX }} />

          <SectionHeader title="APPROVAL" />
          <NavItem id="app_pending" label="Pending" icon={<HourglassEmptyIcon fontSize="small" />} />
          <NavItem id="app_history" label="History" icon={<FactCheckIcon fontSize="small" />} />
          
        </List>
      </Box>

      {/* RIGHT CONTENT AREA - Independent scrolling */}
      <Box sx={{ 
        flexGrow: 1, 
        p: dashboard.content.padding,
        bgcolor: '#fbfbfb', 
        overflowY: 'auto',
        overflowX: 'hidden',
        height: '100%',
        width: '100%',
        '&::-webkit-scrollbar': {
          width: 6,
        },
        '&::-webkit-scrollbar-track': {
          background: '#f1f1f1',
          borderRadius: 3,
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#c1c1c1',
          borderRadius: 3,
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: '#a8a8a8',
        },
      }}>
        {activeMenu === 'my_leave' && <MyLeaveTab balances={balances} myRequests={myRequests} />}
        {(activeMenu === 'balance_overview' || activeMenu === 'team_summary') && <TeamLeaveTab empId={empId} activeMenu={activeMenu} />}
        {activeMenu === 'apply_leave' && <ApplyLeaveTab empId={empId} approvedNHDates={approvedNHDates} showMsg={showMsg} onSuccess={() => { fetchData(); setActiveMenu('apply_status'); }} />}
        {activeMenu === 'apply_status' && <ApplyLeaveTab isStatusOnly myRequests={myRequests} />}
        {(activeMenu === 'app_pending' || activeMenu === 'app_history') && <ApprovalsTab empId={empId} activeMenu={activeMenu} pendingLeaves={pendingLeaves} historyLeaves={historyLeaves} showMsg={showMsg} refreshData={fetchData} />}
      </Box>

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={4000} 
        onClose={() => setSnackbar({ ...snackbar, open: false })} 
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.type} variant="filled" sx={{ width: '100%' }}>
          {snackbar.msg}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default LeaveDashboard;